import { Subject } from 'rxjs';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { SelectItem, LazyLoadEvent, MessageService, ConfirmationService } from 'primeng';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { IBespokeAgreement } from '../../interfaces/bespoke-agreement';
import { IBespokeAgreementFilter } from '../../interfaces/bespoke-agreement-filter';
import { IPageResult } from '../../interfaces/page-result';
import { AgreementService, BespokeAgreementsService, UserService } from '../../services';
import { flatten } from '../../utils/flatten';
import { debounceTime } from 'rxjs/operators';
import { Message } from '../../classes/Message';

@Component({
  selector: 'bespoke-agreements',
  templateUrl: './bespoke-agreements.component.html',
  styleUrls: ['./bespoke-agreements.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class BespokeAgreementsComponent implements OnInit {
  public page: IPageResult<IBespokeAgreement> = {
    items: [],
    total: 0,
  };

  public isCustomer: boolean; //true = bespoke customer, false = bespoke supplier

  public selectedItem: IBespokeAgreement;
  public showViewDialog: boolean = false;
  public filter: Subject<IBespokeAgreementFilter>;
  public filterVal: IBespokeAgreementFilter = { filters: {} };
  public isLoaded: boolean = false;
  public displayDialog: boolean = false;
  public orgsForBespoke: SelectItem[];
  public deptsForBespoke: SelectItem[];
  public dlgSelectedOrgId: any;
  public dlgSelectedDeptId: any;

  bespokeOrgs: any[];
  customerAgreements: Map<number, any[]> = new Map<number, any[]>();

  searchDepartment: string = '';
  searchCustomer: string = '';

  isPricingAdmin: boolean;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private bespokeAgreementsService: BespokeAgreementsService,
    public messageService: MessageService,
    private agreementService: AgreementService,
    private userService: UserService,
  ) {}

  async ngOnInit() {
    this.isPricingAdmin = await this.userService.isUserInRole('PricingAdmin');

    this.route.params.subscribe((params: Params) => {
      switch (params['type']) {
        case 'c':
          this.isCustomer = true;
          break;
        case 's':
          this.isCustomer = false;
          break;
      }

      //
      this.init();
    });
  }

  init() {
    this.isLoaded = false;
    window.scrollTo(0, 0);

    this.initDefaultFilter();

    this.filter = new Subject<IBespokeAgreementFilter>();
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });
    this.filter.next(this.filterVal);

    this.orgsForBespoke = [];
    this.dlgSelectedOrgId = null;

    this.bespokeAgreementsService.getOrganisationsForBespoke(this.isCustomer).then((res) => {
      this.bespokeOrgs = res;

      res.forEach((item) => {
        this.orgsForBespoke.push({ label: item.name, value: item.id });
      });
    });
  }

  load(filter: IBespokeAgreementFilter) {
    this.bespokeAgreementsService.getPage(flatten(filter)).then((result) => {
      this.selectedItem = null;
      this.page.items = result.items;
      console.log(result.items);
      this.page.total = result.total;
      this.isLoaded = true;
    });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;

    this.filter.next(this.filterVal);
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: '',
      sortOrder: 1,
      filters: {
        isCustomer: {
          value: this.isCustomer,
        },
      },
    };
  }

  gotoAgreement(selectedItem) {
    if (selectedItem != null) {
      this.navigateAgreement(selectedItem.organisationId, selectedItem.departmentId);
    }
  }

  private async deleteAgreement(selectedItem) {
    if (selectedItem != null) {
      await this.agreementService.delete(selectedItem?.id);
      this.page.items = this.page.items.filter((x) => x.id !== selectedItem.id);
    }
  }

  async deleteDialog(selectedItem) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this item?',
      header: 'Confirmation',
      accept: async () => {
        await this.deleteAgreement(selectedItem);
      },
    });
  }

  addAgreement() {
    this.showViewDialog = true;
  }

  closeViewDialog() {
    this.showViewDialog = false;
  }

  navigateAgreement(orgId, deptId?: number) {
    if (orgId != null) {
      const route = `/app/agreement/${this.isCustomer ? 'bc' : 'bs'}/${orgId}${
        deptId ? '/' + deptId : ''
      }`;
      this.router.navigate([route], {
        queryParams: { backTo: this.router.url },
      });
    }
  }

  createAgreement() {
    // if we're a customer, check we don't already have an agreement
    if (this.isCustomer) {
      let agr = this.customerAgreements.get(this.dlgSelectedOrgId);
      if (
        (this.dlgSelectedDeptId && agr?.find((x) => x.departmentId == this.dlgSelectedDeptId)) ||
        (!this.dlgSelectedDeptId && agr?.find((x) => !x.departmentId))
      ) {
        this.messageService.add(Message.Error('This customer/department already has an agreement'));
        return;
      }
    }

    this.navigateAgreement(this.dlgSelectedOrgId, this.dlgSelectedDeptId);
  }

  clearFilter() {
    this.initDefaultFilter();
    this.filter.next(this.filterVal);
  }

  search() {
    this.filterVal.first = 0; //fix error - when you set page > 1 and filter, then data not display
    this.filterVal.filters.customerName = { value: this.searchCustomer };
    this.filterVal.filters.department = { value: this.searchDepartment };
    this.filter.next(this.filterVal);
  }

  onSelectedOrgChanged() {
    // populate departments from selected org
    this.deptsForBespoke = [];
    if (this.isCustomer) {
      // get list of bespoke agreement ids this customer has
      if ((this.customerAgreements.get(this.dlgSelectedOrgId)?.length ?? 0) == 0) {
        console.log('Getting customer agreements');
        this.bespokeAgreementsService.getCustomerAgreements(this.dlgSelectedOrgId).then((res) => {
          this.customerAgreements.set(this.dlgSelectedOrgId, res);
        });
      }

      let cust = this.bespokeOrgs.find((x) => x.id == this.dlgSelectedOrgId);

      if (cust) {
        cust.departments.forEach((dept) => {
          this.deptsForBespoke.push({ label: dept.name, value: dept.id });
        });
      }
    }
  }

  reset() {
    this.searchCustomer = '';
    this.searchDepartment = '';
    this.search();
  }
}
